import { Component } from "react"
import { useMemo } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import { map, findIndex, get } from "lodash"
import { CardContent, Paper } from "@mui/material"
import { useThemeChartColors, applyChartColorsToItems } from "hooks/use-theme-chart-colors"
import { PERIOD, DATE, RESOURCE_STATUS } from "helpers/shapes"
import { smallPercentageFormatter, percentageFormatter, makeCurrencyFormatter } from "helpers/formatters/numbers"
import { computeCSVData } from "helpers/csv"
import { METRIC_TYPES_SHAPE } from "helpers/metrics/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"
import AnalyzeHeader from "modules/analyze-header"
import PerformanceDetails from "modules/analyze-performance-details"
import DefaultMetricUnits from "modules/default-metric-units"
import MultiLine from "components/charts/multi-line"
import Tooltip from "components/analyze-tooltip"
import NoData from "components/no-data"
import NotEnoughData from "components/not-enough-data"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import LoadingWrapper from "components/loading-wrapper"
import "./style.scss"

function getGraphValuesFormatter(metricType, currency) {
  return metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? makeNoSymbolCurrencyFormatter(currency) : smallPercentageFormatter
}

function getTooltipValuesFormatter(metricType, currency) {
  return metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? makeCurrencyFormatter(currency) : smallPercentageFormatter
}

function getTabValuesFormatter(metricType, currency) {
  return metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? makeNoSymbolCurrencyFormatter(currency) : percentageFormatter
}

function makeNoSymbolCurrencyFormatter(currency) {
  return makeCurrencyFormatter(currency, { displayCurrencySymbol: false })
}

export class AnalyzePerformance extends Component {
  static propTypes = {
    account: PropTypes.string.isRequired,
    portfolioId: PropTypes.string.isRequired,
    base: PropTypes.object,
    earliestDate: DATE,
    latestDate: DATE,
    startDate: DATE,
    aiSummaryStartDate: DATE,
    endDate: DATE,
    firstDate: DATE,
    selectedDate: DATE,
    period: PERIOD.isRequired,
    data: PropTypes.array.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      disabled: PropTypes.bool.isRequired,
    })).isRequired,
    displayType: PropTypes.string.isRequired,
    viewType: PropTypes.string.isRequired,
    isActive: PropTypes.bool.isRequired,
    setPeriod: PropTypes.func.isRequired,
    setDate: PropTypes.func.isRequired,
    setDisplayType: PropTypes.func.isRequired,
    toggle: PropTypes.func.isRequired,
    focusChartLine: PropTypes.func.isRequired,
    blurChartLine: PropTypes.func.isRequired,
    trackCSVDownload: PropTypes.func.isRequired,
    addNotification: PropTypes.func.isRequired,
    focusedChartLine: PropTypes.string,
    resetDate: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })).isRequired,
    fileName: PropTypes.string.isRequired,
    selectedOption: PropTypes.object.isRequired,
    toggleOnly: PropTypes.func.isRequired,
    only: PropTypes.string,
    filtersVisible: PropTypes.bool.isRequired,
    isOpPro: PropTypes.bool.isRequired,
    datesAdjusted: PropTypes.bool.isRequired,
    selectedViews: PropTypes.array,
    displayAddView: PropTypes.bool.isRequired,
    modelId: PropTypes.string.isRequired,
    addSelectedView: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    availableSelectViewFactors: PropTypes.array.isRequired,
    removeSelectedView: PropTypes.func.isRequired,
    currency: PropTypes.string,
    setMetricType: PropTypes.func.isRequired,
    metricType: METRIC_TYPES_SHAPE.isRequired,
    passthroughResourcesStatus: RESOURCE_STATUS.isRequired,
  }

  componentDidUpdate() {
    const { firstDate, startDate: propsStartDate, selectedDate } = this.props
    const startDate = firstDate || propsStartDate

    if (selectedDate && (selectedDate < startDate)) {
      this.props.resetDate()
    }
  }

  onTabChanged = (_, id) => {
    const { setDisplayType } = this.props
    setDisplayType(id)
  }

  addSelectedView = (factor) => {
    const { addSelectedView, setDisplayType } = this.props
    addSelectedView(factor.id)
    setDisplayType(factor.id)
  }

  handleRemoveSelectedView = (factorId) => {
    const { removeSelectedView, displayType, setDisplayType, options } = this.props
    if (displayType === factorId) {
      const optionIndex = findIndex(options, option => option.id === factorId)
      setDisplayType(get(options, `${optionIndex - 1}.id`))
    }
    removeSelectedView(factorId)
  }

  render() {
    const {
      base,
      earliestDate,
      latestDate,
      setDate,
      selectedDate,
      startDate,
      aiSummaryStartDate,
      endDate,
      firstDate,
      datesAdjusted,
      period,
      data,
      items,
      displayType,
      options,
      toggle,
      focusChartLine,
      blurChartLine,
      focusedChartLine,
      viewType,
      isActive,
      fileName,
      resetDate,
      selectedOption,
      trackCSVDownload,
      toggleOnly,
      only,
      account,
      portfolioId,
      setPeriod,
      addNotification,
      isOpPro,
      selectedViews,
      displayAddView,
      openDialog,
      availableSelectViewFactors,
      currency,
      setMetricType,
      metricType,
      passthroughResourcesStatus,
    } = this.props
    const graphValuesFormatter = getGraphValuesFormatter(metricType, currency)

    let content
    if (!earliestDate || !latestDate) {
      content = <NoData title="Performance Trends" className="graph" />
    } else if (!data || data.length === 0) {
      content = <NotEnoughData portfolio={portfolioId} account={account} isOpPro={isOpPro} />
    } else {
      content = <CardContent className={classnames("card-content")}>
        <Paper className="content">
          <div className="graph">
            <GraphTopBar {...{
              displayAddView,
              openDialog,
              factors: availableSelectViewFactors,
              addView: this.addSelectedView,
              value: displayType,
              onTabChanged: this.onTabChanged,
              tabs: map(options, ({ id, label, value, isSelectedView }) => ({
                key: id,
                value: id,
                removable: isSelectedView,
                onRemoveTab: () => this.handleRemoveSelectedView(id),
                label: <><div>{label}</div><div>{getTabValuesFormatter(metricType, currency)(value)}</div></>,
              })),
              actions: [
                <DefaultMetricUnits key="unit-selector" metric="performance" unit={metricType} onChange={setMetricType} includeUnitSelector />,
                <ExcelDownload key="excel-download" data={computeCSVData(data, items, { percents: metricType !== METRIC_TYPES.CURRENCY_PERFORMANCE })} fileName={fileName} track={trackCSVDownload} />,
              ],
            }}
            />
            <MultiLine
              tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={(v) => v === "--" ? v : getTooltipValuesFormatter(metricType, currency)(v)} />}
              {...{
                tickFormat: graphValuesFormatter,
                formatter: graphValuesFormatter,
                startDate,
                date: selectedDate,
                setDate,
                endDate,
                data,
                onlyItem: only,
                items,
                toggle,
                focusChartLine,
                blurChartLine,
                focusedChartLine,
                toggleOnly,
              }}
            />
          </div>
          <PerformanceDetails
            viewType={viewType}
            displayType={displayType}
            selectedDate={selectedDate}
            selectedOption={selectedOption}
            selectedViews={selectedViews}
            metricType={metricType}
            currency={currency}
          />
        </Paper>
      </CardContent>
    }

    return <div className="modules_analyze-performance">
      <AnalyzeHeader
        selectedId="performance"
        {...{
          account,
          portfolioId,
          adjusted: datesAdjusted,
          base,
          earliestDate,
          latestDate,
          startDate,
          aiSummaryStartDate,
          endDate,
          selectedDate,
          period,
          setPeriod,
          addNotification,
          resetDate,
          firstDate,
          isActive,
          currency,
          changeDisplayPreferenceType: "navigation.analyze.trends",
          showFilterScope: true,
        }}
      />
      <LoadingWrapper status={[passthroughResourcesStatus]}>{content}</LoadingWrapper>
    </div>
  }
}

function AnalyzePerformanceWithTheme(props) {
  const chartColors = useThemeChartColors()
  const items = useMemo(() => applyChartColorsToItems(props.items, chartColors), [props.items, chartColors])
  return <AnalyzePerformance {...props} items={items} />
}

AnalyzePerformanceWithTheme.propTypes = {
  ...AnalyzePerformance.propTypes,
}

export default AnalyzePerformanceWithTheme
